import React from 'react';
import { HelpCircle, X, Trash2 } from 'lucide-react';

import { AssistantStatusIndicator } from '../../components/common/AssistantStatusIndicator';
import { MessageBubble } from '../../components/common/MessageBubble';
import { Button } from '../../components/design-system';
import { useChatSession } from '../../hooks/useChatSession';
import type { ChatCitation } from '../../api/services/chat';
import { CHAT_INSERT_EVENT, consumeChatDraft } from '../../utils/chatDraft';

type CitationSelection = {
  citation: ChatCitation;
  index: number;
};

const buildSources = (
  citations: ChatCitation[] | undefined,
  onSelect: (citation: ChatCitation, index: number) => void,
) =>
  citations?.map((citation, index) => {
    const range =
      citation.end_line !== citation.start_line
        ? `${citation.start_line}-${citation.end_line}`
        : `${citation.start_line}`;
    return {
      id: `${citation.path}-${citation.start_line}-${index}`,
      label: `${citation.path}:${range}`,
      onSelect: () => onSelect(citation, index),
    };
  });

const TIPS = [
  'Reference recent answers when asking follow-up questions',
  'Use specific keywords from your documents for precise results',
  'Provide context to help the retrieval system ground responses',
  'Click on citation badges to see the source excerpts',
];

const ChatPage: React.FC = () => {
  const { messages, isSending, error, sendMessage, clearConversation } = useChatSession();
  const [input, setInput] = React.useState('');
  const [selection, setSelection] = React.useState<CitationSelection | null>(null);
  const [showTips, setShowTips] = React.useState(false);
  const listRef = React.useRef<HTMLDivElement | null>(null);
  const inputRef = React.useRef<HTMLTextAreaElement | null>(null);
  const tipsRef = React.useRef<HTMLDivElement | null>(null);

  React.useEffect(() => {
    inputRef.current?.focus();
  }, []);

  // Close tips popover when clicking outside
  React.useEffect(() => {
    const handleClickOutside = (e: MouseEvent) => {
      if (tipsRef.current && !tipsRef.current.contains(e.target as Node)) {
        setShowTips(false);
      }
    };
    if (showTips) {
      document.addEventListener('mousedown', handleClickOutside);
      return () => document.removeEventListener('mousedown', handleClickOutside);
    }
  }, [showTips]);

  React.useEffect(() => {
    if (typeof window === 'undefined') return;

    const appendDraft = (draft: string) => {
      const trimmed = draft.trim();
      if (!trimmed) return;
      setInput((previous) => {
        if (!previous) return trimmed;
        const prefix = previous.trimEnd();
        const separator = prefix ? '\n\n' : '';
        return `${prefix}${separator}${trimmed}`;
      });
      requestAnimationFrame(() => inputRef.current?.focus());
    };

    const draft = consumeChatDraft();
    if (draft) appendDraft(draft);

    const handleDraftEvent = (event: Event) => {
      const detail = (event as CustomEvent<string>).detail;
      if (typeof detail === 'string') appendDraft(detail);
    };

    window.addEventListener(CHAT_INSERT_EVENT, handleDraftEvent as EventListener);
    return () => window.removeEventListener(CHAT_INSERT_EVENT, handleDraftEvent as EventListener);
  }, []);

  React.useEffect(() => {
    if (!listRef.current) return;
    listRef.current.scrollTo({ top: listRef.current.scrollHeight, behavior: 'smooth' });
  }, [messages, isSending]);

  const handleSubmit = (event: React.FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    if (!input.trim()) return;
    const content = input;
    setInput('');
    setSelection(null);
    void sendMessage(content);
  };

  const handleKeyDown = (event: React.KeyboardEvent<HTMLTextAreaElement>) => {
    if (event.key === 'Enter' && !event.shiftKey) {
      event.preventDefault();
      if (input.trim()) {
        const content = input;
        setInput('');
        setSelection(null);
        void sendMessage(content);
      }
    }
  };

  const handleClear = () => {
    clearConversation();
    setSelection(null);
  };

  return (
    <div className="h-full flex flex-col overflow-hidden bg-neutral-100 p-1.5">
      {/* Minimal header */}
      <header className="flex-shrink-0 flex items-center justify-between px-2 pb-1.5">
        <h1 className="text-base font-semibold text-neutral-800">Chat</h1>
        {messages.length > 0 && (
          <button
            type="button"
            onClick={handleClear}
            className="flex items-center gap-1 text-xs text-neutral-400 hover:text-neutral-600 transition-colors"
          >
            <Trash2 className="w-3 h-3" />
            Clear
          </button>
        )}
      </header>

      {/* Main content */}
      <div className="flex-1 min-h-0 flex gap-1.5 overflow-hidden">
        {/* Chat panel */}
        <div className="flex-1 min-w-0 flex flex-col min-h-0 bg-white rounded-lg border border-neutral-200 overflow-hidden">
          {/* Messages - scrollable */}
          <div
            ref={listRef}
            className="flex-1 min-h-0 overflow-y-auto px-3 py-3 space-y-3"
            role="log"
            aria-live="polite"
          >
            {messages.length === 0 ? (
              <div className="h-full flex flex-col items-center justify-center text-center px-4">
                <div className="text-3xl mb-3">💬</div>
                <p className="text-sm font-medium text-neutral-600">Start a conversation</p>
                <p className="text-xs text-neutral-400 mt-1 max-w-xs">
                  Ask questions about your workspace documents. I'll find relevant information and
                  cite my sources.
                </p>
              </div>
            ) : (
              messages.map((message, index) => (
                <MessageBubble
                  key={`${message.role}-${message.timestamp ?? index}`}
                  role={message.role}
                  timestamp={message.timestamp}
                  content={message.content}
                  sources={
                    message.role === 'assistant'
                      ? buildSources(message.citations, (citation, citationIndex) =>
                          setSelection({ citation, index: citationIndex }),
                        )
                      : undefined
                  }
                />
              ))
            )}

            {/* Thinking indicator */}
            {isSending && (
              <div className="flex items-start gap-2 px-1">
                <AssistantStatusIndicator active prefixLabel="Assistant" density="compact" />
              </div>
            )}
          </div>

          {/* Input area */}
          <div className="flex-shrink-0 border-t border-neutral-100 bg-neutral-50/50 px-3 py-2">
            {error && (
              <div className="mb-2 rounded-lg border border-red-200 bg-red-50 px-2 py-1.5 text-xs text-red-600">
                {error}
              </div>
            )}
            <form onSubmit={handleSubmit} className="flex gap-2 items-end">
              <div className="flex-1 relative">
                <textarea
                  ref={inputRef}
                  value={input}
                  onChange={(e) => setInput(e.target.value)}
                  onKeyDown={handleKeyDown}
                  rows={2}
                  placeholder="Ask a question…"
                  className="w-full resize-none rounded-lg border border-neutral-200 bg-white px-3 py-2 pr-8 text-sm text-neutral-900 placeholder:text-neutral-400 focus:border-blue-300 focus:outline-none focus:ring-1 focus:ring-blue-200"
                />
                {/* Tips button */}
                <div className="absolute right-2 top-2" ref={tipsRef}>
                  <button
                    type="button"
                    onClick={() => setShowTips(!showTips)}
                    className="text-neutral-300 hover:text-neutral-500 transition-colors"
                    aria-label="Show tips"
                  >
                    <HelpCircle className="w-4 h-4" />
                  </button>
                  {/* Tips popover */}
                  {showTips && (
                    <div className="absolute right-0 bottom-full mb-2 w-64 p-3 bg-white rounded-lg border border-neutral-200 shadow-lg z-10">
                      <p className="text-xs font-medium text-neutral-700 mb-2">Tips</p>
                      <ul className="space-y-1.5">
                        {TIPS.map((tip, i) => (
                          <li key={i} className="text-[11px] text-neutral-500 flex gap-1.5">
                            <span className="text-neutral-300">•</span>
                            {tip}
                          </li>
                        ))}
                      </ul>
                    </div>
                  )}
                </div>
              </div>
              <Button
                type="submit"
                variant="primary"
                loading={isSending}
                disabled={isSending || !input.trim()}
                className="flex-shrink-0 h-9"
              >
                Send
              </Button>
            </form>
            <p className="text-[10px] text-neutral-400 mt-1.5 text-center">
              Press Enter to send · Shift+Enter for new line
            </p>
          </div>
        </div>

        {/* Citation details */}
        {selection && (
          <>
            {/* Mobile: bottom-sheet modal (prevents cramped split view) */}
            <div
              className="fixed inset-0 bg-black/40 z-[60] lg:hidden"
              onClick={() => setSelection(null)}
              aria-hidden="true"
            />
            <div
              className="fixed inset-x-0 bottom-0 z-[61] lg:hidden bg-white border-t border-neutral-200 rounded-t-2xl overflow-hidden shadow-2xl"
              role="dialog"
              aria-modal="true"
              aria-label="Citation"
            >
              <div className="flex items-center justify-between px-4 py-3 border-b border-neutral-100 bg-neutral-50/50">
                <span className="text-sm font-medium text-neutral-800">Citation</span>
                <button
                  type="button"
                  onClick={() => setSelection(null)}
                  className="text-neutral-500 hover:text-neutral-700 transition-colors"
                  aria-label="Close citation"
                >
                  <X className="w-5 h-5" />
                </button>
              </div>
              <div className="max-h-[70dvh] overflow-y-auto px-4 py-4">
                <p className="text-xs font-medium text-neutral-800 break-all">
                  {selection.citation.path}
                </p>
                <p className="text-[10px] text-neutral-400 mt-1">
                  Lines {selection.citation.start_line}–{selection.citation.end_line}
                </p>
                <div className="mt-3 p-3 rounded-lg bg-neutral-50 border border-neutral-100">
                  <p className="text-xs leading-relaxed text-neutral-700 whitespace-pre-wrap">
                    {selection.citation.text}
                  </p>
                </div>
              </div>
              <div className="h-[env(safe-area-inset-bottom)] bg-white" aria-hidden="true" />
            </div>

            {/* Desktop: side panel */}
            <div className="hidden lg:flex w-80 flex-shrink-0 flex-col bg-white rounded-lg border border-neutral-200 overflow-hidden animate-in slide-in-from-right-2 duration-200">
              {/* Panel header */}
              <div className="flex-shrink-0 flex items-center justify-between px-3 py-2 border-b border-neutral-100 bg-neutral-50/50">
                <span className="text-xs font-medium text-neutral-700">Citation</span>
                <button
                  type="button"
                  onClick={() => setSelection(null)}
                  className="text-neutral-400 hover:text-neutral-600 transition-colors"
                  aria-label="Close citation"
                >
                  <X className="w-4 h-4" />
                </button>
              </div>

              {/* Panel content */}
              <div className="flex-1 overflow-y-auto p-3">
                <p className="text-xs font-medium text-neutral-800 break-all">
                  {selection.citation.path}
                </p>
                <p className="text-[10px] text-neutral-400 mt-1">
                  Lines {selection.citation.start_line}–{selection.citation.end_line}
                </p>
                <div className="mt-3 p-2 rounded-lg bg-neutral-50 border border-neutral-100">
                  <p className="text-xs leading-relaxed text-neutral-700 whitespace-pre-wrap">
                    {selection.citation.text}
                  </p>
                </div>
              </div>
            </div>
          </>
        )}
      </div>
    </div>
  );
};

export default ChatPage;
